import type { AxiosError } from 'axios';
import { ErrorCategory } from '../types/retry';

/**
 * Classify an error into an ErrorCategory for retry strategy selection.
 *
 * Classification logic:
 * - ColdStart: ECONNREFUSED, ECONNABORTED, 503, 504 (backend starting up)
 * - Timeout: ETIMEDOUT (request timeout)
 * - NetworkError: NETWORK_ERROR, no response (connectivity issues)
 * - PermissionRefresh: 403 (may have dynamically granted permissions)
 * - AuthFailure: 400, 401 (genuine authentication failures)
 * - Unknown: Any other error (treat cautiously)
 *
 * @param error The axios error to classify
 * @param customClassifier Optional custom classifier to override default logic
 * @returns The error category
 *
 * @example
 * ```typescript
 * const category = classifyError(axiosError);
 * if (category === ErrorCategory.ColdStart) {
 *   // Use cold start retry strategy
 * }
 * ```
 */
export function classifyError(
  error: AxiosError,
  customClassifier?: (error: AxiosError) => ErrorCategory
): ErrorCategory {
  // Use custom classifier if provided
  if (customClassifier) {
    return customClassifier(error);
  }

  // Check for cold start scenarios (backend not ready)
  // ECONNABORTED can occur when Azure Container Apps are scaling from 0
  if (error.code === "ECONNREFUSED" || error.code === "ECONNABORTED") {
    return ErrorCategory.ColdStart;
  }

  if (error.response?.status === 503 || error.response?.status === 504) {
    return ErrorCategory.ColdStart;
  }

  // Check for timeout
  if (error.code === "ETIMEDOUT") {
    return ErrorCategory.Timeout;
  }

  // Check for network connectivity issues
  if (error.code === "NETWORK_ERROR" || !error.response) {
    return ErrorCategory.NetworkError;
  }

  // Check for permission refresh scenario (403)
  if (error.response?.status === 403) {
    return ErrorCategory.PermissionRefresh;
  }

  // Check for authentication failures (400, 401)
  if (error.response?.status === 400 || error.response?.status === 401) {
    return ErrorCategory.AuthFailure;
  }

  // Unknown error - treat cautiously
  return ErrorCategory.Unknown;
}
